import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import noop from 'lodash/noop';
import reduce from 'lodash/reduce';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createDocumentResource, { handleStandardErrors } from 'src/actions/shared/createDocumentResource';
import emptyObserverCreator from 'src/actions/shared/emptyObserverCreator';
import Ivr, { IvrNodeMap } from 'src/models/Ivrs/Ivr';
import IvrNode from 'src/models/Ivrs/IvrNode';
import ivrsTreeDataResource, { convertCoordinatesInTreeDataToNumbers } from './ivrsTreeDataResource';
import { NODE_LINKS_MAPPER } from 'src/models/Ivrs/NodeListMapper';
import { NodeScheduleItem } from 'src/models/Ivrs/Nodes/ScheduleNodeDef';
import { START_NODE_ID } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { toastError } from 'src/actions/toast';
import { validateHeaders } from 'src/models/Ivrs/Nodes/Http2NodeDef';
import { validateMenuOptions, validateMenuNode } from 'src/models/Ivrs/Nodes/MenuNodeDef';
import validateType, { translateErrors } from 'src/adapters/validateType';

const DELETED_NODE_MESSAGE = 'This node was deleted. Choose another node or create a new one.';

export const getIvrBaseResource = (endpoint: string, name: string) =>
  createDocumentResource<Ivr>({
    name,
    endpoint,
    observerCreator: emptyObserverCreator,
    reducers: {
      fetchSuccess(state, action) {
        state.loadingState = 'succeeded';
        state.value = action.payload;
      },
      setSelectedNodeId(state, action) {
        state.selectedNodeId = action.payload;
      },
    },
  });

const ivrResource = getIvrBaseResource('v1/orgs/:orgId/configuration/ivrs/:id/versions/:version', 'ivr');

const unload: sharedTypes.AppThunk = () => noop;

const fetchWithPositions: sharedTypes.AppThunk =
  (params: any) =>
  async (dispatch, getState, { gateways }) => {
    const { fetchStarted, fetchSuccess, fetchError, fetchRequestError } = ivrResource.actions;
    const { fetchStarted: treeDataFetchStarted, fetchSuccess: treeDataFetchSuccess } = ivrsTreeDataResource.actions;

    try {
      dispatch(fetchStarted({}));
      dispatch(treeDataFetchStarted({}));
      const result = await gateways.ivr.request(params);
      const treeData = await gateways.ivrsTreeData.request(params);
      const convertedTreeData = convertCoordinatesInTreeDataToNumbers(treeData);
      dispatch(treeDataFetchSuccess(get(convertedTreeData, 'positions', {})));
      dispatch(fetchSuccess(result));
    } catch (error) {
      handleStandardErrors({ error, dispatch, handleDomainError: fetchError, handleDefaultError: fetchRequestError });
    }
  };

const validateConfiguration: sharedTypes.AppThunk = (config: Ivr) => dispatch => {
  const nodes = get(config, 'nodesMap', {});

  const errors = [
    ...reduce(
      nodes,
      (acc: any, node) => {
        const errors = getNodeErrors(node, nodes) as any;
        return [...acc, ...errors];
      },
      []
    ),
    ...getRootErrors(config, nodes),
  ];
  if (isEmpty(nodes)) {
    dispatch(validationError([{ attr: 'nodeMap', code: 'blank' }]));
    return false;
  }
  if (!isEmpty(errors)) {
    dispatch(validationError(errors));
    return false;
  }

  return true;
};

const getNodeErrors = (node: IvrNode, nodesMap: IvrNodeMap) => {
  let errors = [
    // TODO: temporary fix it, uncomment it after return name validation
    // - https://app.clubhouse.io/gladly/story/81463/ad-disable-unique-name-validation-for-ivr-nodes
    // ...validateUniqueNameOrDescription(node, nodeIndex, getState),
    ...validateMenuOptions(node),
    ...validateMenuNode(node),
    ...validateNextNodes(node, nodesMap),
    ...validateHeaders(node),
  ];

  try {
    validateType(IvrNode, node);
  } catch (err) {
    const nodeId = get(node, 'ivrNode.id');
    const translatedErrors = map(translateErrors((err as any).errors), error => ({
      ...error,
      nodeId,
      attr: `nodesMap.${nodeId}.${error.attr}`,
    }));
    errors = [...translatedErrors, ...errors];
  }

  return errors;
};

function getRootErrors(config: Ivr, nodesMap: IvrNodeMap) {
  const nextNodeId = config.rootNodeId || '';
  if (nextNodeId && !has(nodesMap, nextNodeId)) {
    return [
      {
        attr: 'rootNodeId',
        code: 'not_found',
        detail: DELETED_NODE_MESSAGE,
        nodeId: START_NODE_ID,
      },
    ];
  }
  return [];
}

function validateNextNodes(node: IvrNode, nodesMap: IvrNodeMap) {
  const nodeType = get(node, 'type');

  const fieldNames = get(NODE_LINKS_MAPPER, nodeType, []);

  return reduce(
    fieldNames,
    (acc: any, fieldName) => {
      const attr =
        typeof fieldName === 'object'
          ? `ivrNode.${fieldName.prefixPath}.${fieldName.postfixPath}`
          : `ivrNode.${fieldName}`;

      const nextNodeId = get(node, attr, '');

      if (nextNodeId && !has(nodesMap, nextNodeId)) {
        acc.push({
          attr: `nodesMap.${node.ivrNode.id}.${attr}`,
          code: 'not_found',
          detail: DELETED_NODE_MESSAGE,
          nodeId: node.ivrNode.id,
        });
      }

      return acc;
    },
    []
  );
}

const validateNode: sharedTypes.AppThunk = (config: Ivr, nodeId) => dispatch => {
  const nodes = get(config, 'nodesMap', {});
  const errors = getNodeErrors(nodes[nodeId], nodes);

  if (!isEmpty(errors)) {
    dispatch(validationError(errors));
    return false;
  }
  return true;
};

const makePhoneCall: sharedTypes.AppThunk =
  (phoneNumber: string) =>
  async (dispatch, getState, { gateways }) => {
    const { ivr } = getState();

    try {
      await gateways.ivrs.makePhoneCall({ id: ivr.value.id, version: ivr.value.version }, phoneNumber);
    } catch (err) {
      const errorMessage = (err as any).errors?.[0].detail || 'There was an error while calling';
      dispatch(toastError(errorMessage));
    }
  };

const validateSchedule: sharedTypes.AppThunk = (attrs: any) => () => {
  try {
    validateType(NodeScheduleItem, attrs);
  } catch (err) {
    return false;
  }
  return true;
};

ivrResource.effects.fetch = fetchWithPositions;
ivrResource.effects.makePhoneCall = makePhoneCall;
ivrResource.effects.unload = unload;
ivrResource.effects.validateConfiguration = validateConfiguration;
ivrResource.effects.validateNode = validateNode;
ivrResource.effects.validateSchedule = validateSchedule;

const { validationError } = ivrResource.actions;

export default ivrResource;
